import { Component, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EFileService } from '../../efile.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-xml-consol-upload',
    templateUrl: './xml-consol-upload.component.html',
    styleUrls: ['./xml-consol-upload.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class XmlConsolUploadComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};
  filterParams: any = {};
  submissionId = '';
  taxYear = '';
  xmlFileName = '';
  xmlFile: File | null = null;
  loading = false;

  constructor(
    private efileService: EFileService,
    private snackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private submissionService: SubmissionService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.efileService.setBaseUrls(this.baseURLs);
    this.filterParams = normalizeFilterParams(activeScreen);

    // Legacy `XMLConsolUploadController` used `EfileFactory.selectedSubmission`
    // (SUBMISSION_ID + TAX_YEAR). Pattern B deep-links do not carry grid rowData.
    const activeSubmission: any = this.submissionService?.activeSubmission || {};
    const gridRow = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData = {
      ...gridRow,
      SUBMISSION_ID:
        activeSubmission.SUBMISSION_ID ??
        gridRow?.SUBMISSION_ID ??
        gridRow?.submission_id ??
        this.route.snapshot.params?.['id'] ??
        '',
      TAX_YEAR:
        activeSubmission.TAX_YEAR ??
        gridRow?.TAX_YEAR ??
        this.filterParams?.tax_year ??
        this.gtwGlobal.global?.globalParams?.tax_year ??
        '',
    };
    this.submissionId = String(this.rowData.SUBMISSION_ID || '');
    this.taxYear = String(this.rowData.TAX_YEAR || '');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.xmlFile = input?.files?.[0] || null;
  }

  upload(): void {
    if (this.loading || !this.xmlFile || !this.xmlFileName) return;
    if (!this.submissionId || !this.taxYear) {
      this.snackbar.openSnackBar(
        'Submission context is missing. Re-open the submission and try again.',
        'gtw-snackbar--red'
      );
      return;
    }
    this.loading = true;

    this.efileService.uploadXMLKeyId(
      this.submissionId,
      this.xmlFileName,
      this.xmlFile,
      this.taxYear
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.callSuccess === '1') {
          this.snackbar.openSnackBar('The xml was successfully uploaded!', 'gtw-snackbar--green');
          this.xmlFileName = '';
          this.xmlFile = null;
        } else {
          this.snackbar.openSnackBar(res?.errorMessage || 'Upload failed', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.loading = false;
        this.snackbar.openSnackBar('Something went wrong! Please try again.', 'gtw-snackbar--red');
      }
    });
  }
}
